import { useAtom } from 'jotai';
import { useMemo } from 'react';
import {
  Button,
  Flex,
  LabelMenuButton,
  Menu,
  MenuContent,
  type MenuOption,
  MenuTrigger,
  Text,
  theme,
  Tooltip,
} from '@optiaxiom/react';
import { IconXmark } from '@optimizely/axiom-icons';

import { stickyboardFilterAtom } from '@frontend/atoms/stickyboardAtoms';

import { useSections, useStacks } from '../hooks/useStickyboard';

// The empty-string id is the "Unsorted" / "No stack" bucket — cards whose
// effective section / stack id is absent.
const UNBUCKETED = '';

type Dimension = 'sections' | 'stacks';

/**
 * Section + stack filter bar. Two multi-select menus (Axiom `Menu` with
 * `multi` options, so toggling keeps the menu open) plus removable chips and a
 * "Clear all". View-only: it never mutates the board, only narrows what's shown
 * (the predicate lives in StickyboardBoard). Selections persist per-user via
 * `stickyboardFilterAtom`.
 */
export function StickyboardFilterBar() {
  const [filter, setFilter] = useAtom(stickyboardFilterAtom);
  const sections = useSections().data ?? [];
  const stacks = useStacks().data ?? [];

  const toggle = (dim: Dimension, id: string) =>
    setFilter(f => {
      const current = f[dim];
      const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
      return { ...f, [dim]: next };
    });

  const sectionOptions: MenuOption[] = useMemo(
    () => [
      ...sections.map(s => ({
        key: s.section_id,
        label: s.label,
        multi: true,
        selected: filter.sections.includes(s.section_id),
        execute: () => toggle('sections', s.section_id),
      })),
      {
        key: '__unsorted__',
        label: 'Unsorted',
        multi: true,
        selected: filter.sections.includes(UNBUCKETED),
        execute: () => toggle('sections', UNBUCKETED),
      },
    ],
    // toggle is stable enough via setFilter; depend on the inputs that change labels/selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, filter.sections]
  );

  const stackOptions: MenuOption[] = useMemo(
    () => [
      ...stacks.map(s => ({
        key: s.stack_id,
        label: s.label,
        multi: true,
        selected: filter.stacks.includes(s.stack_id),
        execute: () => toggle('stacks', s.stack_id),
      })),
      {
        key: '__nostack__',
        label: 'No stack',
        multi: true,
        selected: filter.stacks.includes(UNBUCKETED),
        execute: () => toggle('stacks', UNBUCKETED),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stacks, filter.stacks]
  );

  const sectionLabel = (id: string) =>
    id === UNBUCKETED ? 'Unsorted' : (sections.find(s => s.section_id === id)?.label ?? id);
  const stackLabel = (id: string) =>
    id === UNBUCKETED ? 'No stack' : (stacks.find(s => s.stack_id === id)?.label ?? id);

  const activeCount = filter.sections.length + filter.stacks.length;

  return (
    <Flex flexDirection="column" gap="8" data-testid="stickyboard-filter-bar">
      <Flex flexDirection="row" gap="8" alignItems="center" flexWrap="wrap">
        <Menu options={sectionOptions}>
          <MenuTrigger asChild>
            <LabelMenuButton label="Sections" data-testid="filter-sections-trigger">
              {filter.sections.length ? `${filter.sections.length} selected` : 'All'}
            </LabelMenuButton>
          </MenuTrigger>
          <MenuContent />
        </Menu>
        <Menu options={stackOptions}>
          <MenuTrigger asChild>
            <LabelMenuButton label="Stacks" data-testid="filter-stacks-trigger">
              {filter.stacks.length ? `${filter.stacks.length} selected` : 'All'}
            </LabelMenuButton>
          </MenuTrigger>
          <MenuContent />
        </Menu>
        {activeCount > 0 && (
          <Button
            size="sm"
            appearance="subtle"
            onClick={() => setFilter({ sections: [], stacks: [] })}
          >
            Clear all
          </Button>
        )}
      </Flex>

      {activeCount > 0 && (
        <Flex flexDirection="row" gap="4" flexWrap="wrap" alignItems="center">
          {filter.sections.map(id => (
            <FilterChip
              key={`sec-${id}`}
              label={sectionLabel(id)}
              onRemove={() => toggle('sections', id)}
            />
          ))}
          {filter.stacks.map(id => (
            <FilterChip
              key={`stk-${id}`}
              label={stackLabel(id)}
              onRemove={() => toggle('stacks', id)}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Flex
      flexDirection="row"
      gap="2"
      alignItems="center"
      style={{
        border: `1px solid ${theme.colors['border.tertiary']}`,
        borderRadius: 9999,
        paddingLeft: 8,
      }}
    >
      <Text fontSize="xs" color="fg.secondary">
        {label}
      </Text>
      <Tooltip content={`Remove ${label}`} delayDuration={0}>
        <Button
          size="sm"
          appearance="subtle"
          aria-label={`Remove ${label} filter`}
          icon={<IconXmark />}
          onClick={onRemove}
        />
      </Tooltip>
    </Flex>
  );
}
